
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Neighborhood {
  name: string;
  zips: string;
  medianPrice: string;
  avgDOM: number;
  speed: 'Fastest' | 'Fast' | 'Moderate' | 'Slow';
  description: string;
  color: string;
  position: { top: string; left: string };
}

const neighborhoods: Neighborhood[] = [
  {
    name: 'West El Paso',
    zips: '79912',
    medianPrice: '$310K–$425K',
    avgDOM: 22,
    speed: 'Fast',
    description: 'Premium prices with educated, selective buyers near UTEP and the Franklin Mountains.',
    color: '#2D6A4F',
    position: { top: '32%', left: '18%' },
  },
  {
    name: 'East El Paso',
    zips: '79936, 79938',
    medianPrice: '$245K–$340K',
    avgDOM: 18,
    speed: 'Fastest',
    description: 'Hottest seller\'s market in the metro. Newer homes, military demand, and top schools.',
    color: '#1B4332',
    position: { top: '48%', left: '72%' },
  },
  {
    name: 'Northeast',
    zips: '79924, 79934',
    medianPrice: '$220K–$310K',
    avgDOM: 26,
    speed: 'Fast',
    description: 'Balanced middle market with strong family demand and Fort Bliss proximity.',
    color: '#40916C',
    position: { top: '18%', left: '52%' },
  },
  {
    name: 'Central El Paso',
    zips: '79902, 79903',
    medianPrice: '$165K–$280K',
    avgDOM: 38,
    speed: 'Moderate',
    description: 'Character homes in historic neighborhoods. Complex sales but unique buyer appeal.',
    color: '#D4A373',
    position: { top: '40%', left: '30%' },
  },
  {
    name: 'Upper Valley',
    zips: '79922, 79932',
    medianPrice: '$280K–$550K+',
    avgDOM: 34,
    speed: 'Moderate',
    description: 'El Paso\'s luxury segment. Higher prices but patience required for the right buyer.',
    color: '#6B705C',
    position: { top: '22%', left: '10%' },
  },
  {
    name: 'Mission Valley',
    zips: '79915',
    medianPrice: '$155K–$220K',
    avgDOM: 42,
    speed: 'Moderate',
    description: 'Affordable area with slower absorption. FHA buyers dominate this market.',
    color: '#B08968',
    position: { top: '58%', left: '38%' },
  },
  {
    name: 'Lower Valley',
    zips: '79907, 79927',
    medianPrice: '$130K–$195K',
    avgDOM: 52,
    speed: 'Slow',
    description: 'Toughest seller market. Older homes, longer wait times, and price-sensitive buyers.',
    color: '#9C6644',
    position: { top: '72%', left: '48%' },
  },
  {
    name: 'Horizon City',
    zips: '79928',
    medianPrice: '$260K–$350K',
    avgDOM: 20,
    speed: 'Fastest',
    description: 'Explosive growth with newer homes. Competes with new construction for buyer attention.',
    color: '#1B4332',
    position: { top: '55%', left: '88%' },
  },
  {
    name: 'Socorro',
    zips: '79927',
    medianPrice: '$195K–$275K',
    avgDOM: 24,
    speed: 'Fast',
    description: 'Rising star for sellers. Growing demand, affordable prices, and improving infrastructure.',
    color: '#40916C',
    position: { top: '68%', left: '72%' },
  },
];

const speedConfig = {
  Fastest: { bg: 'bg-green-100', text: 'text-green-800', bar: 'bg-green-500' },
  Fast: { bg: 'bg-emerald-100', text: 'text-emerald-800', bar: 'bg-emerald-500' },
  Moderate: { bg: 'bg-amber-100', text: 'text-amber-800', bar: 'bg-amber-500' },
  Slow: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-500' },
};

export default function NeighborhoodSelector() {
  const [active, setActive] = useState<number>(1);
  const selected = neighborhoods[active];
  const cfg = speedConfig[selected.speed];
  const maxDOM = 60;

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-4">
          <p className="text-sm uppercase tracking-widest text-gray-500 mb-3 font-semibold">EXPLORE EL PASO</p>
          <h2 className="text-5xl font-bold text-black mb-4">Which Neighborhood Is Your Home In?</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Click any area to see how fast homes sell and what they&apos;re worth. Your neighborhood determines everything.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-14 items-start">
          {/* Left: Area buttons list */}
          <div className="lg:col-span-2 space-y-2">
            {neighborhoods.map((n, i) => (
              <button
                key={n.name}
                onClick={() => setActive(i)}
                className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl text-left transition-all duration-200 cursor-pointer group ${
                  active === i
                    ? 'bg-black text-white shadow-lg'
                    : 'bg-gray-50 text-gray-800 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`w-3 h-3 rounded-full flex-shrink-0 transition-all ${
                      active === i ? 'bg-[#D4AF37] scale-125' : 'bg-gray-300 group-hover:bg-gray-400'
                    }`}
                  ></span>
                  <span className="text-sm font-semibold whitespace-nowrap">{n.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium whitespace-nowrap ${active === i ? 'text-gray-300' : 'text-gray-500'}`}>
                    {n.avgDOM}d
                  </span>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                      active === i
                        ? 'bg-white/20 text-white'
                        : `${speedConfig[n.speed].bg} ${speedConfig[n.speed].text}`
                    }`}
                  >
                    {n.speed}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Right: Detail card */}
          <div className="lg:col-span-3">
            <div className="bg-[#FAF9F6] rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-3xl font-bold text-black mb-1">{selected.name}</h3>
                  <p className="text-sm text-gray-500">ZIP: {selected.zips}</p>
                </div>
                <span className={`text-sm font-bold px-4 py-1.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                  {selected.speed}
                </span>
              </div>

              <p className="text-base text-gray-700 leading-relaxed mb-8">
                {selected.description}
              </p>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-black/5 rounded-lg">
                      <i className="ri-money-dollar-circle-line text-lg text-black"></i>
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Median Price</span>
                  </div>
                  <p className="text-2xl font-bold text-black">{selected.medianPrice}</p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-black/5 rounded-lg">
                      <i className="ri-timer-line text-lg text-black"></i>
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Avg Days on Market</span>
                  </div>
                  <p className="text-2xl font-bold text-black">{selected.avgDOM} days</p>
                </div>
              </div>

              {/* DOM bar visualization */}
              <div className="mb-8">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <span>Speed vs. Metro Average (28 days)</span>
                  <span>{selected.avgDOM} / {maxDOM} days</span>
                </div>
                <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${cfg.bar}`}
                    style={{ width: `${Math.min((selected.avgDOM / maxDOM) * 100, 100)}%` }}
                  ></div>
                  {/* Metro average marker */}
                  <div
                    className="absolute top-0 h-full w-0.5 bg-black/40"
                    style={{ left: `${(28 / maxDOM) * 100}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-gray-400">Faster</span>
                  <span className="text-gray-400">Slower</span>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/get-cash/el-paso-neighborhood-guide-sellers"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  Read Full Neighborhood Guide
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="tel:9157457883"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-black text-sm font-semibold rounded-full hover:bg-[#C9A430] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill"></i>
                  Get Offer for {selected.name}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom link */}
        <div className="text-center mt-12">
          <Link
            to="/get-cash/el-paso-neighborhood-guide-sellers"
            className="inline-flex items-center gap-2 text-base font-semibold text-black underline hover:text-gray-600 transition-colors cursor-pointer"
          >
            View the complete neighborhood-by-neighborhood breakdown
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
